import React, { useRef, useState } from "react";
import CardForm from "../../components/CardForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "../../hooks/queryParams";
import queryString from "query-string";
import { Button, Container, useDisclosure, useToast } from "@chakra-ui/react";
import {
  PaymentDetail,
  useDeletePaymentDetailMutation,
  useSetDefaultPaymentDetailMutation,
} from "../../generated/graphql";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { AddIcon } from "@chakra-ui/icons";
import TableListTitle from "../../components/TableList/TableListTitle";
import { TableImperativeHandle } from "../../components/DefaultTable";
import { FormMutationType } from "../../components/FullWidthModal/interface";
import PaymentDetailForm from "./PaymentDetailForm";
import { IFilterPaymentDetail } from "../../components/TablePaymentDetail/interface";
import TablePaymentDetail from "../../components/TablePaymentDetail";
import { useErrorMessage } from "../../hooks/errorMessage";

const ListPaymentDetail: React.FC = () => {
  const location = useLocation();

  const { pathname } = location;

  const navigate = useNavigate();

  const toast = useToast();

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [selectedId, setSelectedId] = useState("");

  const { confirmation, setIsLoading, close } = useConfirmation();

  const { queryParams } = useQueryParams<
    IFilterPaymentDetail & { page: string }
  >();

  const onPageChange = (page: number) => {
    const url = queryString.stringifyUrl({
      url: pathname,
      query: {
        ...queryParams,
        page,
      },
    });
    navigate(url);
  };

  const onFilterSubmit = (params: IFilterPaymentDetail) => {
    const url = queryString.stringifyUrl({
      url: pathname,
      query: {
        ...queryParams,
        ...params,
        page: 1,
      },
    });

    navigate(url);
  };

  const [deletePaymentDetailMutation] = useDeletePaymentDetailMutation();

  const onDelete = (row: PaymentDetail) => {
    confirmation({
      title: "Confirmation",
      description: `Are you sure want to delete this payment detail?`,
      callback: async () => {
        try {
          setIsLoading(true);
          await deletePaymentDetailMutation({
            variables: { id: row.id },
          });
          tableRef.current?.refresh();
          toast({
            status: "success",
            description: "Successfully deleted!",
          });
        } catch (e: any) {
          if (e.message === "cannot_delete_the_only_one") {
            toast({
              status: "error",
              description: "You should have minimal 1 Payment Detail!",
            });
          } else if (e.message === "constraint_violation") {
            toast({
              status: "error",
              description: "Cannot delete in used Payment Detail!",
            });
          } else {
            toast({
              status: "error",
              description: e.message,
            });
          }
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  const tableRef = useRef<TableImperativeHandle<PaymentDetail>>(null);

  const { showErrorMessage } = useErrorMessage();

  const [setDefault] = useSetDefaultPaymentDetailMutation();
  const onChangeDefault = (row: PaymentDetail) => {
    confirmation({
      title: "Confirmation",
      description: `Are you sure want to change the default?`,
      callback: async () => {
        try {
          setIsLoading(true);
          await setDefault({
            variables: { id: row.id },
          });
          tableRef.current?.refresh();
          toast({
            status: "success",
            description: "Successfully Updated!",
          });
        } catch (e) {
          showErrorMessage(e);
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  return (
    <>
      <Container maxW="container.xl">
        <CardForm>
          <TableListTitle
            title="List Payment Details"
            actionComponents={
              <Button
                onClick={() => onOpenForm()}
                leftIcon={<AddIcon />}
                size="sm"
              >
                Create New Payment Detail
              </Button>
            }
          />

          <TablePaymentDetail
            ref={tableRef}
            page={
              queryParams?.page
                ? parseInt(queryParams.page as unknown as string)
                : 1
            }
            onPageChange={onPageChange}
            onChangeDefault={onChangeDefault}
            filterParams={queryParams}
            onFilterSubmit={onFilterSubmit}
            hasMenu
            onDelete={onDelete}
            onEdit={(row) => {
              setSelectedId(row.id);
              onOpenForm();
            }}
          />
        </CardForm>
      </Container>
      <PaymentDetailForm
        isOpen={isOpenForm}
        onClose={(res) => {
          if (res?.type === FormMutationType.UPDATE) {
            tableRef.current?.updateRow(res.data as PaymentDetail);
          } else if (res?.type === FormMutationType.CREATE) {
            tableRef.current?.refresh();
            onPageChange(1);
          }
          setSelectedId("");
          onCloseForm();
        }}
        id={selectedId}
      />
    </>
  );
};

export default ListPaymentDetail;
